import { useLocation, useNavigate } from "react-router-dom";

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { StyledAddTicket } from "./AddTicket.styled";
import { Button, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import AddCustomer from "@features/tickets/entities/customers/add";
import { useEffect, useState } from "react";
import { deleteEmptyFieldsFromObject } from "@shared/lib/utils";
import { Account, AccountAdd, AccountAddSchema } from "@models/account";
import { SubmitHandler, useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getToastErrorMessage, handleErrorWithBoundary } from "@shared/lib/errors";
import { toast } from 'react-toastify';
import AddItem from "@features/tickets/entities/items/add";
import AddCar from "@features/tickets/entities/cars/add";
import AddWork from "@features/tickets/entities/works/add";
import { Work, WorkForm, WorkFormSchema } from "@models/work";
import AddIncome from "@features/tickets/entities/incomes/add";
import AddOutcome from "@features/tickets/entities/outcomes/add";
import AddSystem from "@features/tickets/entities/system/add";
import AddBank from "@features/tickets/entities/banks/add";
import AddCashBox from "@features/tickets/entities/cash-boxes/add";
import { RootState } from "@app/store";
import { useSelector } from "react-redux";
import { setIncomesTable, setOutcomesTable } from "@features/tables/store/tables.slice";
import { SelectTable } from "@models/table";
import { setCustomDataArray } from "@features/tickets/shared/utils/customData";
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from "@emotion/react";
import { CssField } from "@shared/ui/mui-input/MuiInput.styled";
import { Car, CarAdd, CarAddSchema } from "@models/car";
import { Item, ItemAdd, ItemAddSchema } from "@models/item";
import { useErrorBoundary } from "react-error-boundary";
import { TicketRoute } from "@models/types";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { TicketHebType } from "@models/types";
import { editLastBread, getLastAvailableLink } from "@features/tickets/shared/utils/breadcrumbs";
import Recommendation from "@features/tickets/shared/components/recommendation/Recommendation";
import {
    useAddAccountMutation,
    useLazyGetNextAccountNumberQuery,
} from "@features/tickets/api/accounts.api";
import { useAddItemMutation, useLazyGetNextItemNumberQuery } from "@features/tickets/api/items.api";
import { useAddWorkMutation, useLazyGetNextWorkNumberQuery } from "@features/tickets/api/works.api";
import { useAddCarMutation, useLazyGetNextCarNumberQuery } from "@features/tickets/api/cars.api";

type Ticket = {
    url: TicketRoute,
    name: string,
    type: TicketHebType
}

const ticketTypeList: Ticket[] = [
    {
        url: "customers",
        name: "לקוחות/ספקים",
        type: "לקוח-ספק"
    },
    {
        url: "items",
        name: "פריטים/מלאי",
        type: "פריט"
    },
    {
        url: "works",
        name: "עבודות",
        type: "עבודה"
    },
    {
        url: "cars",
        name: "רכבים",
        type: "רכב"
    },
    {
        url: "incomes",
        name: "הכנסות",
        type: "הכנסה"
    },
    {
        url: "outcomes",
        name: "הוצאות",
        type: "הוצאה"
    },
    {
        url: "banks",
        name: "בנקים",
        type: "בנק"
    },
    {
        url: "cash-boxes",
        name: "קופה",
        type: "קופה"
    },
    {
        url: "system",
        name: "מערכת",
        type: "מערכת"
    },
];


function AddTicket() {

    const incomesTable = useSelector((state: RootState) => state.tables.incomesTable);
    const outcomesTable = useSelector((state: RootState) => state.tables.outcomesTable);

    const { showBoundary } = useErrorBoundary();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const ticketURL = location.pathname.split("/")[1];
    const ticket: Ticket | undefined = ticketTypeList.find(ticket => ticket.url === ticketURL);

    const [ticketNumber, setTicketNumber] = useState<number>();
    const [triggerNextAccountNumber] = useLazyGetNextAccountNumberQuery();
    const [triggerNextItemNumber] = useLazyGetNextItemNumberQuery();
    const [triggerNextWorkNumber] = useLazyGetNextWorkNumberQuery();
    const [triggerNextCarNumber] = useLazyGetNextCarNumberQuery();
    const [addAccount] = useAddAccountMutation();
    const [addItem] = useAddItemMutation();
    const [addWork] = useAddWorkMutation();
    const [addCar] = useAddCarMutation();


    const customerForm = useForm<AccountAdd>({
        resolver: zodResolver(AccountAddSchema),
        defaultValues: {
            vat_include: true,
            ...location.state?.document_account
        }
    });

    const itemForm = useForm<ItemAdd>({
        resolver: zodResolver(ItemAddSchema),
        defaultValues: {
            ...location.state?.item
        }
    });

    const carForm = useForm<CarAdd>({
        resolver: zodResolver(CarAddSchema),
        defaultValues: {
            ...location.state?.car
        }
    });

    const workForm = useForm<WorkForm>({
        resolver: zodResolver(WorkFormSchema),
        defaultValues: {
            ...location.state?.work
        }
    });

    const incomeForm = useForm<AccountAdd>({
        resolver: zodResolver(AccountAddSchema),
        defaultValues: {
            vat_include: true,
            ...location.state?.income
        }
    });

    const outcomeForm = useForm<AccountAdd>({
        resolver: zodResolver(AccountAddSchema),
        defaultValues: {
            vat_include: true,
            ...location.state?.outcome
        }
    });

    const bankForm = useForm<AccountAdd>({
        resolver: zodResolver(AccountAddSchema),
        defaultValues: {
            ...location.state?.bank
        }
    });

    const cashBoxForm = useForm<AccountAdd>({
        resolver: zodResolver(AccountAddSchema),
        defaultValues: {
            ...location.state?.cashBox
        }
    });

    const systemForm = useForm<AccountAdd>({
        resolver: zodResolver(AccountAddSchema),
        defaultValues: {
            ...location.state?.system
        }
    });


    useEffect(() => {

        if (ticketURL === "items") {
            getNextItemTicketNumber();
        }

        else if (ticketURL === "works") {
            getNextWorkTicketNumber();
        }

        else if (ticketURL === "cars") {
            getNextCarTicketNumber();
        }

        else {
            getNextTicketNumber();
        }
    }, [ticketURL]);


    const getNextTicketNumber = async () => {
        try {
            setTicketNumber(undefined);
            if (!ticket?.type) {
                return;
            }
            const number = await triggerNextAccountNumber(ticket.type as Account["account_type"]).unwrap();
            setTicketNumber(number);
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }

    const getNextItemTicketNumber = async () => {
        try {
            setTicketNumber(undefined);
            const number = await triggerNextItemNumber().unwrap();
            setTicketNumber(number);
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }

    const getNextWorkTicketNumber = async () => {
        try {
            setTicketNumber(undefined);
            const number = await triggerNextWorkNumber().unwrap();
            setTicketNumber(number);
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }

    const getNextCarTicketNumber = async () => {
        try {
            setTicketNumber(undefined);
            const number = await triggerNextCarNumber().unwrap();
            setTicketNumber(number);
        } catch (error) {
            handleErrorWithBoundary(error, showBoundary);
        }
    }

    function handleSelectChange(e: SelectChangeEvent<string>) {

        const ticket: Ticket | undefined = ticketTypeList.find(type => type.url === e.target.value);

        if(ticket){
            navigate(`/${ticket.url}/add`, {
                replace: true,
                state: {
                    breadcrumbData: [
                        {
                            name: "כרטסות",
                        },
                        {
                            name: ticket.name,
                            link: `/${ticket.url}`
                        },
                        {
                            name: `הוספת כרטיס`
                        }
                    ]
                }}
            );
        }
    }

    // function returnFunc() {
    //     navigate(location.state.breadcrumbData[location.state.breadcrumbData.length-2].link, {
    //         replace: true,
    //         state: {
    //             ...location.state,
    //             // from: `/documents/add`,
    //             // document_settings_id: location.state.document_settings_id,

    //             // works: location.state.works,

    //             // items: location.state.items,

    //             // customer: location.state.customer

    //             breadcrumbData: location.state.breadcrumbData.slice(0, -1)
    //         }
    //     });
        
    // }


    const addNewTicket = async () => {

        if (!ticketNumber) {
            return;
        }


        if (ticketURL === "customers") {
            try {

                customerForm.setValue("card_number", ticketNumber);
                customerForm.setValue("account_type", "לקוח-ספק");

                const customDataArray = customerForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    customerForm.setValue("custom_data", customDataArray);
                }

                customerForm.handleSubmit(onSubmitAccount)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "items") {

            try {

                const customDataArray = itemForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    itemForm.setValue("custom_data", customDataArray);
                }

                itemForm.handleSubmit(onSubmitItem)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "works") {

            try {

                const customDataArray = workForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    workForm.setValue("custom_data", customDataArray);
                }

                workForm.handleSubmit(onSubmitWork)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "cars") {

            try {

                const customDataArray = carForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    carForm.setValue("custom_data", customDataArray);
                }

                carForm.handleSubmit(onSubmitCar)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "incomes") {
            try {

                incomeForm.setValue("card_number", ticketNumber);
                incomeForm.setValue("account_type", "הכנסה");

                const customDataArray = incomeForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    incomeForm.setValue("custom_data", customDataArray);
                }

                incomeForm.handleSubmit(onSubmitAccount)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "outcomes") {
            try {

                outcomeForm.setValue("card_number", ticketNumber);
                outcomeForm.setValue("account_type", "הוצאה");

                const customDataArray = outcomeForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    outcomeForm.setValue("custom_data", customDataArray);
                }

                outcomeForm.handleSubmit(onSubmitAccount)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "banks") {
            try {

                bankForm.setValue("card_number", ticketNumber);
                bankForm.setValue("account_type", "בנק");

                const customDataArray = bankForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    bankForm.setValue("custom_data", customDataArray);
                }

                bankForm.handleSubmit(onSubmitAccount)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "cash-boxes") {
            try {

                cashBoxForm.setValue("card_number", ticketNumber);
                cashBoxForm.setValue("account_type", "קופה");

                const customDataArray = cashBoxForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    cashBoxForm.setValue("custom_data", customDataArray);
                }

                cashBoxForm.handleSubmit(onSubmitAccount)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }

        if (ticketURL === "system") {
            try {

                systemForm.setValue("card_number", ticketNumber);
                systemForm.setValue("account_type", "מערכת");

                const customDataArray = systemForm.getValues("custom_data");
                setCustomDataArray(customDataArray);

                if (customDataArray) {
                    systemForm.setValue("custom_data", customDataArray);
                }

                systemForm.handleSubmit(onSubmitAccount)();

            } catch (error) {
                console.error("Form submission failed:", error);
            }
        }


    }

    const onSubmitAccount: SubmitHandler<AccountAdd> = (data) => {

        // const dataToSend = deleteEmptyFieldsFromObject(data);
        const dataToSend = data;

        const addPromise = async () => {
            const accountAdded: Account = await addAccount(dataToSend).unwrap();
            addIncomeOrOutcomeToStore(accountAdded);

            if(location.state?.breadcrumbData){
                navigate(getLastAvailableLink(location.state.breadcrumbData), {
                    replace: true,
                    state: {
                        ...location.state,
                        document_account: {...accountAdded, id: undefined, account_id: accountAdded.id },
                        breadcrumbData: location.state.breadcrumbData.slice(0,-1)
                    }
                });

                return;
            }

            setTicketNumber(prev => (prev || 0) + 1);
            customerForm.reset();
        }


        toast.promise(
            addPromise,
            {
                pending: 'מוסיף כרטיס...',
                success: 'הכרטיס נוסף בהצלחה',
                error: {
                    render({ data }) {
                        return getToastErrorMessage(data, "add account");
                    }
                }
            }
        )
    };

    const onSubmitItem: SubmitHandler<ItemAdd> = (data) => {

        const dataToSend = deleteEmptyFieldsFromObject(data);

        const addPromise = async () => {
            const itemAdded: Item = await addItem(dataToSend).unwrap();

            if (location.state?.breadcrumbData) {

                const updatedItems = [...location.state.items];
                updatedItems[location.state.itemRowIndex] = itemAdded;

                navigate(getLastAvailableLink(location.state.breadcrumbData), {
                    replace: true,
                    state: {
                        ...location.state,
                        items: updatedItems,
                        breadcrumbData: location.state.breadcrumbData.slice(0,-1)
                    }
                });

                return;
            }

            setTicketNumber(prev => (prev || 0) + 1);
            itemForm.reset();
        }


        toast.promise(
            addPromise,
            {
                pending: 'מוסיף כרטיס...',
                success: 'הכרטיס נוסף בהצלחה',
                error: {
                    render({ data }) {
                        // When the promise reject, data will contains the error
                        return getToastErrorMessage(data, "add item");
                    }
                }
            }
        )
    };

    const onSubmitCar: SubmitHandler<CarAdd> = (data) => {

        const dataToSend = deleteEmptyFieldsFromObject(data);

        const addPromise = async () => {
            const addedCar: Car = await addCar(dataToSend).unwrap();

            if (location.state?.breadcrumbData) {
                navigate(getLastAvailableLink(location.state.breadcrumbData), {
                    replace: true,
                    state: {
                        ...location.state,
                        document_account: {...addedCar.customer, id: undefined, account_id: addedCar.customer?.id },
                        car: {...addedCar, id: undefined, car_id: addedCar.id },
                        breadcrumbData: location.state.breadcrumbData.slice(0,-1)
                    }
                });

                return;
            }

            setTicketNumber(prev => (prev || 0) + 1);
            carForm.reset();
        }


        toast.promise(
            addPromise,
            {
                pending: 'מוסיף כרטיס...',
                success: 'הכרטיס נוסף בהצלחה',
                error: {
                    render({ data }) {
                        // When the promise reject, data will contains the error
                        return getToastErrorMessage(data, "add car");
                    }
                }
            }
        )
    };

    const onSubmitWork: SubmitHandler<WorkForm> = (data) => {

        const dataToSend = deleteEmptyFieldsFromObject(data);

        const addPromise = async () => {
            const workAdded: Work = await addWork(dataToSend).unwrap();

            if (location.state?.breadcrumbData) {

                const updatedWorks = [...location.state.works];
                updatedWorks[location.state.workRowIndex] = workAdded;


                navigate(getLastAvailableLink(location.state.breadcrumbData), {
                    replace: true,
                    state: {
                        ...location.state,
                        works: updatedWorks,
                        breadcrumbData: location.state.breadcrumbData.slice(0,-1)

                    }
                });

                return;
            }

            setTicketNumber(prev => (prev || 0) + 1);
            workForm.reset();

        }


        toast.promise(
            addPromise,
            {
                pending: 'מוסיף כרטיס...',
                success: 'הכרטיס נוסף בהצלחה',
                error: {
                    render({ data }) {
                        // When the promise reject, data will contains the error
                        return getToastErrorMessage(data, "add work");
                    }
                }
            }
        )
    };


    function addIncomeOrOutcomeToStore(account: Account): void {

        if (account.account_type === "הכנסה") {
            if (incomesTable) {
                const incomeTableToAdd: SelectTable = {
                    "index": account.card_number,
                    "name": account.account_name
                }
                setIncomesTable([...incomesTable, incomeTableToAdd]);
            }
        }
        if (account.account_type === "הוצאה") {
            if (outcomesTable) {
                const outcomeTableToAdd: SelectTable = {
                    "index": account.card_number,
                    "name": account.account_name
                }
                setOutcomesTable([...outcomesTable, outcomeTableToAdd]);
            }
        }
    }

    function createState(){
        return {
            ...location.state,
            customer: customerForm.getValues(),
            item: itemForm.getValues(),
            car: carForm.getValues(),
            work: workForm.getValues(),
            income: incomeForm.getValues(),
            outcome: outcomeForm.getValues(),
            bank: bankForm.getValues(),
            cashBox: cashBoxForm.getValues(),
            system: systemForm.getValues(),
        }
    }

    function createBreadcrumbData(state: any){

        const breadcrumbData = [
            {
                name: "כרטסות",
            },
            {
                name: `${ticket?.name ?? ""}`,
                link: `/${ticketURL}`,
            },
            {
                name: "הוספת כרטיס",
                link: `/${ticketURL}/add`,
                state: state
            },
        ];

        return [...(editLastBread(state?.breadcrumbData, `/${ticketURL}/add`, state) ?? breadcrumbData)];
    }


    function setStructure() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/${ticketURL}/structure/form`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }]
            }
        });
    }

    function browseCustomerForCar() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/customers`, {
            replace: true,
            state: {
                ...currentState,
                customerSelectFor: "car",
                breadcrumbData: [...breadcrumbData, { name: "בחירת לקוח" }]
            }
        });
    }

    function getForm(ticket: Ticket): UseFormReturn<AccountAdd> | undefined{
        switch (ticket.url) {
            case "customers":
                return customerForm;

            case "incomes":
                return incomeForm;

            case "outcomes":
                return outcomeForm;

            case "banks":
                return bankForm;

            case "cash-boxes":
                return cashBoxForm;

            case "system":
                return systemForm;
        }
    }


    const accountForm = ticket ? getForm(ticket) : undefined;

    return (
        <StyledAddTicket>
            <Typography variant="h5" component="span">
                הוספת כרטיס {ticket?.type ?? ""}
            </Typography>
            {
                !ticketNumber ? <Skeleton variant="rectangular" height={500} animation="wave" sx={{ bgcolor: theme.colors.section_color }} /> :
                    <div>
                        <div className="choose-ticket-div">
                            <Box sx={{ minWidth: 120 }}>
                                <CssField>
                                    <FormControl>
                                        <InputLabel id="ticket-add-select-label">כרטסת</InputLabel>
                                        <Select
                                            labelId="ticket-add-select-label"
                                            id="ticket-add-select"
                                            value={ticketURL}
                                            label="ticket"
                                            onChange={handleSelectChange}
                                        >
                                            {ticketTypeList.map((type, index) => (
                                                <MenuItem key={index} value={type.url}>{type.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </CssField>

                            </Box>
                            <span>מספר כרטיס: {ticketNumber}</span>
                            <Tooltip title="הגדרת שדות">
                                <IconButton onClick={setStructure} sx={{ color: theme.colors.main_color }}>
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>

                        </div>
                        <div className="form">
                            {ticketURL === "customers" &&
                                <AddCustomer useForm={customerForm} />
                            }
                            {ticketURL === "items" &&
                                <AddItem useForm={itemForm} />
                            }
                            {ticketURL === "works" &&
                                <AddWork useForm={workForm} />
                            }
                            {ticketURL === "cars" &&
                                <AddCar useForm={carForm} onBrowseCustomer={browseCustomerForCar} />
                            }
                            {ticketURL === "incomes" &&
                                <AddIncome useForm={incomeForm} />
                            }
                            {ticketURL === "outcomes" &&
                                <AddOutcome useForm={outcomeForm} />
                            }
                            {ticketURL === "banks" &&
                                <AddBank useForm={bankForm} />
                            }
                            {ticketURL === "cash-boxes" &&
                                <AddCashBox useForm={cashBoxForm} />
                            }
                            {ticketURL === "system" &&
                                <AddSystem useForm={systemForm} />
                            }
                        </div>
                    </div>
            }
            <div className="buttons">
                <Button variant="contained" onClick={addNewTicket}>שמירה</Button>
                <ReturnButton/>
                {accountForm ? <Recommendation useForm={accountForm} /> : null}
            </div>


        </StyledAddTicket>

    )
}

export default AddTicket